import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { finishTarget, reviewKey, urlOrigin } from '../../../lib/site-review';
import {
  boundConversationFor,
  closeSiteEditFlow,
  expectSiteChat,
  useSiteEditFlow,
  type SiteEditFlow,
} from '../../../lib/site-edit-store';
import { Modal } from '../../Modal';
import { Button, TextButton, Typography } from '../ui';
import { SiteReviewFrame } from './SiteReviewFrame';
import { useSiteChat } from './use-site-edit';
import styles from './SiteEditHost.module.css';

/**
 * The Space presence's site flows that are not a link (contract §1): the TAKEOVER and MOVE offers, and a phone's
 * full-screen "Review site". Opened through `openSiteEditFlow`, drawn here once, from AppFrame.
 */
export function SiteEditHost() {
  const flow = useSiteEditFlow();
  if (!flow) return null;
  return flow.kind === 'review' ? <SiteReviewSheet flow={flow} /> : <SiteEditOffer flow={flow} />;
}

function SiteEditOffer({ flow }: { flow: Exclude<SiteEditFlow, { kind: 'review' }> }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { send, draft, text } = useSiteChat(flow);
  const done = () => closeSiteEditFlow();

  if (flow.kind === 'takeover') {
    return (
      <Modal
        open
        onClose={done}
        size="small"
        maxHeightPx={650}
        scrollBody
        title={t('siteEdit.takeover.title', { productName })}
        footer={
          <div className={styles.footer}>
            <Button
              variant="outlined"
              onClick={() => {
                done();
                send({ key: 'siteEdit.takeover.githubMessage' });
              }}
              data-testid="siteTakeoverGithub"
            >
              {t('siteEdit.takeover.connectGithub')}
            </Button>
            <Button
              onClick={() => {
                done();
                draft(text({ key: 'siteEdit.takeover.zipMessage' }), null, true);
              }}
              data-testid="siteTakeoverZip"
            >
              {t('siteEdit.takeover.uploadZip')}
            </Button>
          </div>
        }
      >
        <div className={styles.body} data-testid="siteTakeoverOffer">
          <Typography variant="p2" color="primary">
            {t('siteEdit.takeover.lead', { productName })}
          </Typography>
          <ul className={styles.points}>
            {(['once', 'noWriteBack', 'stopDeploys'] as const).map((k) => (
              <li key={k}>
                <Typography variant="p2" color="primary">
                  {t(`siteEdit.takeover.${k}`, { productName })}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    );
  }

  const { offer } = flow;
  const lead = offer.state === 'compare' ? 'siteEdit.move.readyLead' : offer.state === 'done' ? 'siteEdit.move.doneLead' : 'siteEdit.move.lead';
  const primary =
    offer.state === 'compare' ? (
      <Button href={offer.compareUrl} external target="_blank" rel="noopener noreferrer" onClick={done} data-testid="siteMovePrimary">
        {t('siteEdit.move.compare')}
      </Button>
    ) : offer.state === 'done' ? (
      <Button
        onClick={() => {
          done();
          send({ key: 'workspace.website.editPrompt', values: { url: flow.siteRef } });
        }}
        data-testid="siteMovePrimary"
      >
        {t('siteEdit.move.openChat')}
      </Button>
    ) : (
      <Button
        onClick={() => {
          done();
          send({ key: 'siteEdit.move.message' });
        }}
        data-testid="siteMovePrimary"
      >
        {t('siteEdit.move.start', { productName })}
      </Button>
    );
  return (
    <Modal
      open
      onClose={done}
      size="small"
      maxHeightPx={650}
      scrollBody
      title={t('siteEdit.move.title', { productName })}
      footer={
        <div className={styles.footer}>
          {offer.adminUrl && (
            <TextButton size="small" href={offer.adminUrl} external target="_blank" rel="noopener noreferrer" onClick={done} data-testid="siteMoveAdmin">
              {offer.platformName ? t('siteEdit.move.editOn', { platform: offer.platformName }) : t('siteEdit.move.editOnPlatform')}
            </TextButton>
          )}
          {primary}
        </div>
      }
    >
      <div className={styles.body} data-testid="siteMoveOffer" data-state={offer.state}>
        <Typography variant="p2" color="primary">
          {t(lead, { productName })}
        </Typography>
      </div>
    </Modal>
  );
}

/**
 * A phone's "Review site" (and a computer's whose chat has no room for the pane): the live site full screen, with the
 * same review as the pane. Finish closes it and SENDS the message to the site's chat — the newest one bound to this
 * site, else a new chat in the Space bound to it, the one Edit opens — and shows that chat. Never a composer draft:
 * opened from the Space strip, the only mounted composer was Home's new chat. The iframe handshake is the same on a
 * phone browser.
 */
function SiteReviewSheet({ flow }: { flow: Extract<SiteEditFlow, { kind: 'review' }> }) {
  const { sendText, send } = useSiteChat(flow);
  const bound = boundConversationFor(flow.workspaceId, flow.previewUrl);
  // The page under the sheet must not scroll behind it (iOS Safari would otherwise move both).
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  if (typeof document === 'undefined' || !urlOrigin(flow.previewUrl)) return null;
  return createPortal(
    <div className={styles.sheet} role="dialog" aria-modal="true" data-testid="siteReviewSheet">
      <SiteReviewFrame
        previewUrl={flow.previewUrl}
        reviewKey={reviewKey(flow.previewUrl, bound, flow.workspaceId)}
        layout="sheet"
        conversationId={bound}
        onFinish={(message) => {
          const target = finishTarget(null, bound);
          closeSiteEditFlow();
          if (target.kind === 'new') expectSiteChat(flow.workspaceId, flow.previewUrl);
          const conversationId = target.kind === 'existing' ? target.conversationId : null;
          sendText(message, conversationId);
          return conversationId;
        }}
        onClose={closeSiteEditFlow}
        onAskRepublish={() => {
          closeSiteEditFlow();
          send({ key: 'siteReview.republishMessage', values: { url: flow.previewUrl } }, bound);
        }}
      />
    </div>,
    document.body,
  );
}
